import React, { memo, useState, useCallback } from "react";
import { FiArrowUp, FiArrowDown, FiTrash2, FiEdit2 } from "react-icons/fi";
import { Modal } from "newui/components/ui/Modal";
import type { ScoreRange } from "../../types/ScoringInstruction.type";
import { ScoringInstructionsEditModal } from "./EditModal";

interface ScoringInstructionsRowProps {
	error?: string;
	index: number;
	isFirst: boolean;
	isLast: boolean;
	range: ScoreRange;
	totalScore: number;
	onDelete: () => void;
	onMove: (direction: "up" | "down") => void;
	onToggleType: () => void;
	onUpdate: (updates: Partial<ScoreRange>) => void;
}

const ScoringInstructionsRowInner: React.FC<ScoringInstructionsRowProps> = ({
	range,
	index,
	isFirst,
	isLast,
	totalScore,
	error,
	onUpdate,
	onDelete,
	onMove,
	onToggleType,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

	const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(e.target.value, 10);
		if (!isNaN(value)) {
			onUpdate({ min: value });
			if (range.isSingleValue) {
				onUpdate({ min: value, max: value });
			}
		}
	};

	const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(e.target.value, 10);
		if (!isNaN(value)) {
			onUpdate({ max: value });
		}
	};

	const handleEditSave = useCallback(
		(values: { description: string; isSingleValue: boolean; max: number; min: number; }) => {
			onUpdate(values);
		},
		[onUpdate],
	);

	const scoreLabel = range.isSingleValue
		? `Score: ${range.min}`
		: `Score: ${range.min}–${range.max}`;

	return (
		<>
			<tr
				className={`border-b border-gray-200 ${error ? "bg-red-50" : "hover:bg-gray-50"}`}
				data-testid={`scoring-row-${index}`}
			>
				<td className="px-3 py-2 text-center font-roboto text-sm text-gray-500">
					{index + 1}
				</td>
				<td className="px-3 py-2">
					<div className="flex items-center gap-2">
						{range.isSingleValue ? (
							<input
								type="number"
								value={range.min}
								onChange={handleMinChange}
								min={0}
								max={totalScore}
								className="w-20 rounded border border-gray-300 px-2 py-1 font-roboto text-sm focus:border-royal-blue focus:outline-none"
								data-testid={`scoring-row-${index}-min`}
							/>
						) : (
							<>
								<input
									type="number"
									value={range.min}
									onChange={handleMinChange}
									min={0}
									max={totalScore}
									className="w-20 rounded border border-gray-300 px-2 py-1 font-roboto text-sm focus:border-royal-blue focus:outline-none"
									data-testid={`scoring-row-${index}-min`}
								/>
								<span className="font-roboto text-sm text-gray-400">–</span>
								<input
									type="number"
									value={range.max}
									onChange={handleMaxChange}
									min={0}
									max={totalScore}
									className="w-20 rounded border border-gray-300 px-2 py-1 font-roboto text-sm focus:border-royal-blue focus:outline-none"
									data-testid={`scoring-row-${index}-max`}
								/>
							</>
						)}
						<button
							type="button"
							onClick={onToggleType}
							className="rounded px-2 py-1 font-roboto text-xs text-gray-500 hover:bg-gray-100"
							title={range.isSingleValue ? "Switch to range" : "Switch to single value"}
							data-testid={`scoring-row-${index}-toggle-type`}
						>
							{range.isSingleValue ? "Use Range" : "Use Single"}
						</button>
					</div>
				</td>
				<td className="px-3 py-2">
					<div
						className="group relative cursor-pointer rounded border border-transparent px-3 py-2 hover:border-gray-200 hover:bg-gray-50"
						onClick={() => setIsEditing(true)}
						data-testid={`scoring-row-${index}-description`}
					>
						<div
							className="font-roboto text-sm text-gray-700 prose prose-sm max-w-none"
							dangerouslySetInnerHTML={{
								__html: range.description || '<span class="text-gray-400">Click to add description...</span>',
							}}
						/>
						<div className="absolute right-2 top-2 hidden group-hover:block">
							<FiEdit2 size={14} className="text-gray-400" />
						</div>
					</div>
				</td>
				<td className="px-3 py-2">
					<div className="flex items-center gap-1">
						<button
							type="button"
							onClick={() => onMove("up")}
							disabled={isFirst}
							className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-30"
							title="Move up"
							data-testid={`scoring-row-${index}-move-up`}
						>
							<FiArrowUp size={16} />
						</button>
						<button
							type="button"
							onClick={() => onMove("down")}
							disabled={isLast}
							className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-30"
							title="Move down"
							data-testid={`scoring-row-${index}-move-down`}
						>
							<FiArrowDown size={16} />
						</button>
						<button
							type="button"
							onClick={() => setIsConfirmingDelete(true)}
							className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
							title="Delete row"
							data-testid={`scoring-row-${index}-delete`}
						>
							<FiTrash2 size={16} />
						</button>
					</div>
				</td>
			</tr>
			{error && (
				<tr>
					<td colSpan={4} className="px-3 pb-2">
						<span className="font-roboto text-xs text-red-500">{error}</span>
					</td>
				</tr>
			)}

			<ScoringInstructionsEditModal
				isOpen={isEditing}
				scoreLabel={scoreLabel}
				totalScore={totalScore}
				initialValues={{
					min: range.min,
					max: range.max,
					isSingleValue: range.isSingleValue,
					description: range.description,
				}}
				onSave={handleEditSave}
				onClose={() => setIsEditing(false)}
			/>

			<Modal
				isOpen={isConfirmingDelete}
				onClose={() => setIsConfirmingDelete(false)}
				title="Delete Score Range"
				iconType="warning"
				closeOnEsc
				size="sm"
				hideCloseButton
				closeOnOverlayClick
				primaryButton={{
					text: "Delete",
					onClick: () => {
						setIsConfirmingDelete(false);
						onDelete();
					},
				}}
				secondaryButton={{
					text: "Cancel",
					onClick: () => setIsConfirmingDelete(false),
				}}
			>
				<div>Are you sure you want to delete {scoreLabel}?</div>
			</Modal>
		</>
	);
};

export const ScoringInstructionsRow = memo(ScoringInstructionsRowInner);
